import { getLocaleInfo } from '../../lang/locales'

const MILES_TO_KM = 1.60934

export function getDefaultWithinForLocale(
  defaultMiles: number,
  locale: string = 'en-gb',
): number {
  const localeInfo = getLocaleInfo(locale)
  return localeInfo.distanceUnit === 'km'
    ? Number((defaultMiles * MILES_TO_KM).toFixed(1))
    : defaultMiles
}

/**
 * Helper function to determine the current range value using standard fallbacks
 *
 * @param options - Configuration options
 * @param options.searchStore - The search store instance with context
 * @param options.isKilometers - Whether the user's locale uses kilometers
 * @param options.area - Optional GeoCircle with rangeMiles property
 * @param options.defaultWithinRange - Default range in miles to use as last resort
 * @returns Current range value in the appropriate unit (miles or kilometers)
 */
export function getCurrentRangeValue({
  searchStore,
  isKilometers,
  area,
  defaultWithinRange,
}: {
  searchStore?: {
    context?: { results?: { within?: { mi: number; km: number } } }
  }
  isKilometers: boolean
  area?: { rangeMiles?: number }
  defaultWithinRange: number
}): number {
  let currentRange: number

  // Try to use search results first
  if (searchStore?.context?.results?.within) {
    currentRange = isKilometers
      ? searchStore.context.results.within.km
      : searchStore.context.results.within.mi
  }
  // Fall back to area if available
  else if (area?.rangeMiles !== undefined) {
    currentRange = isKilometers
      ? area.rangeMiles * MILES_TO_KM
      : area.rangeMiles
  }
  // Use default as last resort
  else {
    currentRange = defaultWithinRange
    if (isKilometers) {
      currentRange = currentRange * MILES_TO_KM
    }
  }

  // Round to ensure clean integers
  return Math.round(currentRange)
}

/**
 * Calculate proposed distance values for low results components
 * Handles localization between miles and kilometers based on the isKilometers flag
 *
 * @param {Object} options - Configuration options
 * @param {number} options.currentRange - Current distance range in the unit determined by isKilometers
 *                                        (miles if isKilometers=false, kilometers if isKilometers=true)
 * @param {number} [options.step=5] - Increment between proposed distances (specified in MILES, converted to km if needed)
 * @param {number} [options.maxDistanceFromCurrent=40] - Maximum distance to add beyond currentRange
 *                                                      (specified in MILES, converted to km if needed)
 * @param {number} [options.topLimit=95] - Absolute maximum distance to propose
 *                                        (specified in MILES, converted to km if needed)
 * @param {boolean} [options.isKilometers=false] - Whether to use kilometers instead of miles
 *                                                If true, all mile-based constants are converted to kilometers
 *                                                and results will be in kilometers
 *
 * @returns {number[]} Array of proposed distances in the same unit as currentRange
 *                     (miles if isKilometers=false, kilometers if isKilometers=true)
 */
export function getProposedDistances({
  currentRange,
  step = 5,
  maxDistanceFromCurrent = 40,
  topLimit = 95,
  isKilometers = false,
}: {
  currentRange: number
  step?: number
  maxDistanceFromCurrent?: number
  topLimit?: number
  isKilometers?: boolean
}): number[] {
  const propositions: number[] = []

  // Convert all mile-based values to kilometers if needed
  // IMPORTANT: currentRange should already be in the correct unit (miles or km)
  // based on the isKilometers flag. The calling component is responsible for
  // providing currentRange in the appropriate unit.
  if (isKilometers) {
    currentRange = Math.round(currentRange)
    step = Math.round(step * MILES_TO_KM)
    maxDistanceFromCurrent = Math.round(maxDistanceFromCurrent * MILES_TO_KM)
    topLimit = Math.round(topLimit * MILES_TO_KM)
  }

  for (
    let index = currentRange + step;
    index <= currentRange + maxDistanceFromCurrent && index <= topLimit;
    index = index + step
  ) {
    propositions.push(Math.round(index))
  }

  return propositions
}
